import type { Product } from '@packages/gql/generated/shopping/graphql';
import { ServiceLayer } from '@packages/modules';
import { Card, CardContent, CardHeader, Dialog, Image, Stack, Typography } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/*
 * INFO: As of right now the data structure for productHints and legalHints is the same, so the components are mostly identical.
 * The data for these two might change in the future and then the two components would have to be split again. For this reason we decided to split into seperate cards them from the start.
 */

export type ProductHintsCardProps = {
  /**
   * Product hints array from given product.
   */
  productHintsData: Product['productHints'];
};

const messages = defineMessages({
  productHintsCardHeader: {
    id: 'productHintsCard.header',
    defaultMessage: 'Hinweise',
  },
});

/**
 * ProductHintsCard component displays all product hints
 * */
export const ProductHintsCard = ({ productHintsData }: ProductHintsCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeServiceLayerId, setActiveServiceLayerId] = useState(
    productHintsData[0].serviceLayerId,
  );
  const dispatchGTMEvent = useTracking();

  const handleClick = (
    serviceLayerId: Product['productHints'][0]['serviceLayerId'],
    text: (typeof productHintsData)[0]['text'],
  ) => {
    setIsDialogOpen(true);
    setActiveServiceLayerId(serviceLayerId);

    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: text,
    });
  };
  const { formatMessage } = useIntl();

  return (
    <>
      <Card>
        <CardHeader
          title={formatMessage(messages.productHintsCardHeader)}
          sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
        />
        <CardContent>
          <Stack spacing={1.5}>
            {productHintsData.map((productHint, idx) => {
              const hasServiceLayerId =
                productHint.serviceLayerId !== null && productHint.serviceLayerId.length > 0;

              return (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={
                    hasServiceLayerId
                      ? () => handleClick(productHint.serviceLayerId, productHint.text)
                      : undefined
                  }
                  role="button"
                  sx={{ cursor: hasServiceLayerId ? 'pointer' : 'default' }}
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                >
                  {/* TODO: get correct alt text */}
                  <Image src={productHint.iconUrl} alt="#" height={24} width={24} />
                  <Typography variant="body2">{productHint.text}</Typography>
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      {activeServiceLayerId !== null && (
        <Dialog
          title={formatMessage(messages.productHintsCardHeader)}
          open={isDialogOpen}
          dialogContent={<ServiceLayer serviceLayerId={activeServiceLayerId!} />}
          handleClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
};
